import { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "motion/react";
import {
  categoryStartIndexes,
  scrollToStoryStep,
  serviceTimeline,
} from "@/config/service-timeline";
import ServiceVisual from "../services/service-visual";
import { useHardwareAcceleration } from "@/hooks/use-hardware-acceleration";
import LiquidGlass from "../ui/liquid-glass";
import ServiceProgress from "../ui/service-progress";
import RevealText, { cinematicEase } from "../ui/reveal-text";
import { cn } from "@/lib/utils";

export default function DesktopServices() {
  const storyRef = useRef<HTMLElement>(null);
  const [activeStep, setActiveStep] = useState(0);
  const reduceMotion = useReducedMotion();
  const hasHardwareAcceleration = useHardwareAcceleration();
  const motionDisabled = Boolean(reduceMotion) || !hasHardwareAcceleration;
  const pointerX = useMotionValue(0);
  const pointerY = useMotionValue(0);
  const smoothX = useSpring(pointerX, { stiffness: 90, damping: 20 });
  const smoothY = useSpring(pointerY, { stiffness: 90, damping: 20 });
  const textX = useTransform(smoothX, (v) => v * 14);
  const textY = useTransform(smoothY, (v) => v * 14);
  const imageX = useTransform(smoothX, (v) => v * 36);
  const imageY = useTransform(smoothY, (v) => v * 28);
  const { scrollYProgress } = useScroll({
    target: storyRef,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (progress) => {
    const nextStep = Math.min(
      serviceTimeline.length - 1,
      Math.floor(Math.max(0, progress) * serviceTimeline.length)
    );
    setActiveStep((currentStep) =>
      currentStep === nextStep ? currentStep : nextStep
    );
  });

  useEffect(() => {
    if (motionDisabled) {
      pointerX.set(0);
      pointerY.set(0);
      return;
    }

    const handleMouseMove = (event: MouseEvent) => {
      pointerX.set((event.clientX - window.innerWidth / 2) / window.innerWidth);
      pointerY.set(
        (event.clientY - window.innerHeight / 2) / window.innerHeight
      );
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, [motionDisabled, pointerX, pointerY]);

  const currentStep = serviceTimeline[activeStep]!;
  const { category, categoryIndex, slide, slideIndex } = currentStep;
  const storyHeight = Math.max(520, serviceTimeline.length * 48);
  const slideKey = `${categoryIndex}-${slideIndex}`;

  const jumpToCategory = (index: number) => {
    if (!storyRef.current) return;
    scrollToStoryStep(
      storyRef.current,
      categoryStartIndexes[index]!,
      serviceTimeline.length
    );
  };

  return (
    <section
      id="services"
      data-desktop-section="services"
      className="px-8 lg:px-12"
    >
      <article
        ref={storyRef}
        className="relative mx-auto w-full max-w-6xl"
        style={{ minHeight: `${storyHeight}vh` }}
      >
        <div className="sticky top-24 h-[calc(100dvh-6rem)] py-6">
          <div className="flex h-full flex-col">
            <div className="mb-5 flex shrink-0 items-end gap-4">
              <div className="relative h-[4.5rem] w-[7.5rem] shrink-0 overflow-hidden">
                <AnimatePresence mode="popLayout" initial={false}>
                  <motion.span
                    key={categoryIndex}
                    initial={{ y: motionDisabled ? 0 : "100%", opacity: 0 }}
                    animate={{ y: "0%", opacity: 1 }}
                    exit={{ y: motionDisabled ? 0 : "-100%", opacity: 0 }}
                    transition={{
                      duration: motionDisabled ? 0.35 : 0.7,
                      ease: cinematicEase,
                    }}
                    className="text-outline absolute inset-0 text-[4.5rem] font-medium leading-none tracking-[-0.04em]"
                    aria-hidden="true"
                  >
                    {String(categoryIndex + 1).padStart(2, "0")}
                  </motion.span>
                </AnimatePresence>
              </div>
              <div className="min-w-0 flex-1 pb-2">
                <div className="relative h-10 overflow-hidden">
                  <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                      key={category.title}
                      exit={{ opacity: 0, y: motionDisabled ? 0 : -14 }}
                      transition={{ duration: motionDisabled ? 0.15 : 0.25 }}
                      className="absolute inset-0"
                    >
                      <RevealText
                        as="h2"
                        text={category.title}
                        stagger={0.04}
                        duration={0.7}
                        className="whitespace-nowrap text-[clamp(1.4rem,2.6vw,2.2rem)] font-normal leading-none tracking-[-0.02em]"
                      />
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>

            <LiquidGlass className="min-h-0 w-full flex-1 bg-white/[0.06]">
              <div className="h-full p-3">
                <div className="relative flex h-full min-h-0 w-full select-none items-center justify-between gap-10 overflow-hidden rounded-[18px] bg-[#071019]/80 px-12 py-8">
                  {/* Stage lighting */}
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.035)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.035)_1px,transparent_1px)] bg-[size:48px_48px] [mask-image:radial-gradient(ellipse_at_70%_50%,black,transparent_75%)]"
                  />
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -right-24 top-1/2 h-[120%] w-[65%] -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgba(20,156,255,0.22),rgba(222,255,119,0.05)_60%,transparent)] blur-2xl"
                  />

                  <motion.div
                    style={{ x: textX, y: textY }}
                    className="relative z-10 max-w-[26rem] text-left"
                    aria-live="polite"
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div
                        key={slideKey}
                        exit={{
                          opacity: 0,
                          y: motionDisabled ? 0 : -16,
                          filter: motionDisabled ? "none" : "blur(6px)",
                        }}
                        transition={{ duration: motionDisabled ? 0.15 : 0.18 }}
                      >
                        <RevealText
                          as="p"
                          text={slide.title}
                          stagger={0.018}
                          duration={0.65}
                          className="text-[clamp(1.6rem,2.6vw,2.2rem)] font-light leading-[1.2] tracking-[-0.02em]"
                        />
                      </motion.div>
                    </AnimatePresence>
                  </motion.div>

                  <motion.div
                    style={{ x: imageX, y: imageY }}
                    className="relative z-10 flex h-full min-h-0 w-full max-w-[26rem] items-center justify-center"
                  >
                    <AnimatePresence mode="popLayout" initial={false}>
                      <motion.div
                        key={slideKey}
                        className="absolute inset-0 flex items-center justify-center"
                        initial={{
                          opacity: 0,
                          scale: motionDisabled ? 1 : 1.08,
                          y: motionDisabled ? 0 : 24,
                          filter: motionDisabled ? "none" : "blur(14px)",
                        }}
                        animate={{
                          opacity: 1,
                          scale: 1,
                          y: 0,
                          filter: motionDisabled ? "none" : "blur(0px)",
                        }}
                        exit={{
                          opacity: 0,
                          scale: motionDisabled ? 1 : 0.95,
                          y: motionDisabled ? 0 : -18,
                          filter: motionDisabled ? "none" : "blur(10px)",
                        }}
                        transition={{
                          duration: motionDisabled ? 0.4 : 0.75,
                          ease: cinematicEase,
                        }}
                      >
                        <ServiceVisual image={slide.image} />
                      </motion.div>
                    </AnimatePresence>
                  </motion.div>
                </div>
              </div>
            </LiquidGlass>

            <div className="mt-5 flex shrink-0 items-center gap-4">
              <span className="min-w-24 font-mono text-xs text-white/50">
                {String(activeStep + 1).padStart(2, "0")} /{" "}
                {String(serviceTimeline.length).padStart(2, "0")}
              </span>
              <ServiceProgress
                activeCategoryIndex={categoryIndex}
                scrollProgress={scrollYProgress}
                onSelectCategory={jumpToCategory}
              />
              <div className="flex min-w-36 items-center justify-end gap-2 text-xs text-white/55">
                <span>Scroll to advance</span>
                <span className={cn("flex", !motionDisabled && "nudge-down")}>
                  <Icon
                    icon="material-symbols:arrow-downward-rounded"
                    className="text-lg text-white/70"
                  />
                </span>
              </div>
            </div>
          </div>
        </div>

        <ol className="sr-only">
          {serviceTimeline.map((step) => (
            <li key={`${step.category.title}-${step.slide.title}`}>
              {step.category.title}: {step.slide.title}
            </li>
          ))}
        </ol>
      </article>
    </section>
  );
}
